import { TableCard } from "@/shared/components/tables/table-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import {
  ListRequest,
  useRelationshipsRelationshipListQuery,
} from "@/services/backend/relationships/api/service";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { BranchName } from "@/services/backend/branch/components/branch-name";
import { formatDate } from "@/shared/lib/utilities/date";
import { ServiceObjectName } from "@/services/backend/customers/components/service-object-name";
import { BranchFilter } from "@/services/backend/branch/components/branch-filter";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { ServiceObjectFilter } from "@/services/backend/customers/components/service-object-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { CustomerFilter } from "@/services/backend/customers/components/customer-filter";
import { DateBetweenFilter } from "@/shared/components/filter/date-between-filter";
import { ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";

interface RelationshipRef {
  id: string;
  customerId: string;
}

export function RelationshipsTable({
  request,
  onRequestChange,
  hideBorder = false,
  hidePagination = false,
  renderRowLink,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  hideBorder?: boolean;
  hidePagination?: boolean;
  renderRowLink: (
    relationship: RelationshipRef,
    children: ReactNode,
  ) => ReactNode;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useRelationshipsRelationshipListQuery(request);

  const emptyList = !isLoading && !error && list!.relationships.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Objekt")}</TableHead>
              <TableHead>{t("Niederlassung")}</TableHead>
              <TableHead>{t("Dienstleistungen")}</TableHead>
              <TableHead>{t("Start")}</TableHead>
              <TableHead>{t("Ende")}</TableHead>
              <TableHead>{t("Nächste Sollstellung")}</TableHead>
              <TableHead>{t("Vertragsstatus")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={7} />}
            {error && <TableRtkErrorRow error={error} colSpan={7} />}
            {emptyList && <TableEmptyRow colSpan={7} />}
            {list?.relationships.map((relationship) => (
              <TableRow key={relationship.id}>
                <TableCell className="py-0">
                  <Button
                    asChild
                    variant="link"
                    size="sm"
                    className="whitespace-normal"
                  >
                    {renderRowLink(
                      relationship,
                      <ServiceObjectName id={relationship.serviceObjectId} />,
                    )}
                  </Button>
                </TableCell>
                <TableCell>
                  <BranchName branchId={relationship.branchId} />
                </TableCell>
                <TableCell>{relationship.serviceKinds.join(", ")}</TableCell>
                <TableCell>{formatDate(relationship.start)}</TableCell>
                <TableCell>{formatDate(relationship.end)}</TableCell>
                <TableCell>
                  {relationship.nextCommitmentAt
                    ? formatDate(relationship.nextCommitmentAt)
                    : t("Keine Zusage")}
                </TableCell>
                <TableCell>tbd</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) => {
            onRequestChange({ ...request, limit, offset });
          }}
          className="ml-auto"
        />
      )}
    </div>
  );
}

export function RelationshipsTableFilters({
  request,
  onRequestChange,
  hideCompanyFilter = false,
  hideBranchFilter = false,
  hideCustomerFilter = false,
  hideServiceObjectFilter = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  hideCompanyFilter?: boolean;
  hideBranchFilter?: boolean;
  hideCustomerFilter?: boolean;
  hideServiceObjectFilter?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <CompanyFilter
        filter={request.company}
        onFilterChange={(company) => onRequestChange({ ...request, company })}
        hidden={hideCompanyFilter}
      />
      <BranchFilter
        filter={request.branch}
        onFilterChange={(branch) => onRequestChange({ ...request, branch })}
        companyIds={
          request.company?.active ? request.company.values : undefined
        }
        hidden={hideBranchFilter}
      />
      <CustomerFilter
        filter={request.customer}
        onFilterChange={(customer) => onRequestChange({ ...request, customer })}
        hidden={hideCustomerFilter}
      />
      <ServiceObjectFilter
        filter={request.serviceObject}
        onFilterChange={(serviceObject) =>
          onRequestChange({ ...request, serviceObject })
        }
        customerIds={
          request.customer?.active ? request.customer.values : undefined
        }
        hidden={hideServiceObjectFilter}
      />
      <DateBetweenFilter
        label={t("Start")}
        filter={request.start}
        onFilterChange={(start) => onRequestChange({ ...request, start })}
      />
      <DateBetweenFilter
        label={t("Ende")}
        filter={request.end}
        onFilterChange={(end) => onRequestChange({ ...request, end })}
      />
      <OrderBySelect
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Start"), value: "start" },
          { label: t("Ende"), value: "end" },
          { label: t("Nächste Sollstellung"), value: "nextCommitmentAt" },
        ]}
      />
    </div>
  );
}
